// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// The facts row at the top of the alarm and anomaly detail pages: the five cells
// of the approved design (Event ID, Time, Location, Source, SLA target). Data
// quality was dropped: AEM has no quality signal, so the cell was a constant. Shared so the anomaly page can't drift back to raw enum values
// (AB#35958, AB#36229).

import React from 'react';
import type { Alarm } from '../../types/alarm';
import { useSiteTime } from '../../hooks/useSiteTime';
import { formatElapsedMinutes } from '../../utils/time';
import { slaStatus } from '../../utils/slaStatus';
import { SEVERITY } from '../../theme/severity';

export default function AlarmMetaGrid({
  alarm,
  extra = [],
}: {
  alarm: Alarm;
  extra?: [string, string | undefined][];
}) {
  const { formatIn } = useSiteTime();

  const eventClock = formatIn(alarm.siteId, alarm.eventTimestamp, 'clock', '') || undefined;
  const timeValue =
    eventClock && alarm.raisedAgo != null
      ? `${eventClock} (${formatElapsedMinutes(alarm.raisedAgo)} ago)`
      : eventClock;

  const sla = slaStatus(alarm);
  const sevLabel = SEVERITY[alarm.sev]?.label ?? alarm.sev;

  const meta: [string, string | undefined, string?][] = [
    ['Event ID', alarm.ruleId ?? alarm.id, 'alarm-event-id'],
    ['Time', timeValue, 'alarm-time'],
    ['Location', alarm.location, 'alarm-location'],
    ['Source', alarm.category, 'alarm-source'],
    ...extra,
  ];

  return (
    <div className="ald-metagrid">
      {meta.map(([k, v, testId]) => (
        <div key={k} className="ald-metaitem" role="group" aria-label={k}>
          <div className="ald-metakey">{k}</div>
          <div className="ald-metaval" data-testid={testId}>
            {v ?? '—'}
          </div>
        </div>
      ))}
      <div className="ald-metaitem" role="group" aria-label={`SLA target (${sevLabel})`}>
        <div className="ald-metakey">SLA target ({sevLabel})</div>
        <div className="ald-metaval">
          {sla.targetText && (
            <>
              {sla.targetText}
              {' · '}
            </>
          )}
          <span className={`ald-sla-status ald-tone--${sla.tone}`}>{sla.statusText}</span>
        </div>
      </div>
    </div>
  );
}
